import React, { useEffect, useState } from 'react';
import apiService from '../../services/ApiService';
import { getVehiclePassportStats, type VehiclePassportStats } from '../../services/api/passport';

interface VehiclePassportStripProps {
  vehicleId: number;
}

/**
 * "N people have seen this build in person" — public, opt-in aggregate.
 * Additive: renders nothing while loading, on error, or when the owner has
 * not opted into public counts. Never exposes scanner identities.
 */
const VehiclePassportStrip: React.FC<VehiclePassportStripProps> = ({ vehicleId }) => {
  const [stats, setStats] = useState<VehiclePassportStats | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!vehicleId) return;
    (async () => {
      try {
        const data = await getVehiclePassportStats(vehicleId);
        if (!cancelled && data.public_count_enabled) {
          setStats(data);
          void apiService.trackEvent('passport_vehicle_strip_viewed', {
            vehicle_id: vehicleId,
            metadata: { seen_in_person_count: data.seen_in_person_count },
          });
        }
      } catch {
        // Backend not available yet, or feature disabled — stay silent.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vehicleId]);

  if (!stats || !stats.public_count_enabled || stats.seen_in_person_count <= 0) {
    return null;
  }

  const count = stats.seen_in_person_count;
  const label = count === 1 ? 'person has seen this build in person' : 'people have seen this build in person';

  return (
    <section className="card px-5 py-4 flex items-center gap-3" aria-label="Bilt Passport seen-in-person count">
      <svg className="w-5 h-5 text-orange-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
      <div className="min-w-0">
        <p className="text-sm font-medium text-white">
          <span className="tabular-nums">{count.toLocaleString()}</span> {label}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">Verified Bilt Passport encounters — Event verified or Staff verified.</p>
      </div>
    </section>
  );
};

export default VehiclePassportStrip;
